'use client';

import Link from 'next/link';
import { useBasePath } from '@/providers/BasePathProvider';

export function PrivacyScreen() {
  const { buildPath } = useBasePath();

  return (
    <main className="min-h-screen bg-white px-4 py-12 flex justify-center">
      <div className="w-full max-w-3xl">
        {/* Back */}
        <Link href={buildPath('/')} className="text-sm text-hprimary hover:underline">
          ← Back to Home
        </Link>

        {/* Header */}
        <h1 className="mt-4 text-3xl font-bold text-hsecondary">Privacy Policy</h1>
        <p className="mt-1 text-sm text-gray-500">Last Updated: December 15, 2025</p>

        {/* HIPAA Notice */}
        <div className="mt-6 border-l-4 border-l-hprimary bg-hprimary/10 p-4 text-sm text-hsecondary">
          <h2 className="text-md font-bold">HIPAA Compliance Notice:</h2>
          Holmdel Pharmacy Care is committed to protecting your privacy and complies with the Health
          Insurance Portability and Accountability Act (HIPAA) and all applicable privacy
          regulations.
        </div>

        {/* 1 */}
        <section className="mt-8">
          <h2 className="text-lg font-bold text-hsecondary">1. Information We Collect</h2>

          <h3 className="mt-4 font-bold text-hsecondary">Personal Information</h3>
          <p className="mt-2 pl-5 text-sm text-hmuted">
            When you create an account or use our services, we may collect:
          </p>
          <ul className="mt-2 list-disc pl-10 text-sm text-hmuted space-y-1">
            <li>Name, email address, phone number</li>
            <li>Mailing address and delivery information</li>
            <li>Date of birth and age verification</li>
            <li>Payment information (processed securely by third parties)</li>
          </ul>

          <h3 className="mt-4 font-bold text-hsecondary">Protected Health Information (PHI)</h3>
          <p className="mt-2 pl-5 text-sm text-hmuted">
            For pharmacy services, we collect health-related information including:
          </p>
          <ul className="mt-2 list-disc pl-10 text-sm text-hmuted space-y-1">
            <li>Prescription information and medication history</li>
            <li>Insurance information</li>
            <li>Allergies and medical conditions</li>
            <li>Healthcare provider information</li>
            <li>Emergency contact information</li>
          </ul>

          <h3 className="mt-4 font-bold text-hsecondary">Automatically Collected Information</h3>
          <ul className="mt-2 list-disc pl-10 text-sm text-hmuted space-y-1">
            <li>Browser type and version</li>
            <li>Device information and IP address</li>
            <li>Pages visited and time spent on our website</li>
            <li>Referring websites and exit pages</li>
          </ul>
        </section>

        {/* 2 */}
        <section className="mt-6">
          <h2 className="text-lg font-semibold text-hsecondary">2. How We Use Your Information</h2>

          <h3 className="mt-3 font-bold text-hsecondary">Pharmacy Services</h3>
          <ul className="mt-2 list-disc pl-10 text-sm text-hmuted space-y-1">
            <li>Processing and dispensing prescriptions</li>
            <li>Verifying insurance coverage and processing claims</li>
            <li>Providing medication counseling and drug interaction checks</li>
            <li>Coordinating with your healthcare providers</li>
            <li>Maintaining your medication history</li>
          </ul>

          <h3 className="mt-4 font-bold text-hsecondary">E-commerce and General Services</h3>
          <ul className="mt-2 list-disc pl-10 text-sm text-hmuted space-y-1">
            <li>Processing orders and arranging delivery or pickup</li>
            <li>Communicating about orders and services</li>
            <li>Sending important updates and notifications</li>
            <li>Improving our website and services</li>
            <li>Fraud prevention and security</li>
          </ul>

          <h3 className="mt-4 font-bold text-hsecondary">Marketing Communications (Optional)</h3>
          <p className="mt-2 text-sm text-hmuted">
            With your consent, we may send promotional emails about new products, special offers, or
            health tips. You can opt out at any time.
          </p>
        </section>

        {/* 3 */}
        <section className="mt-6">
          <h2 className="text-lg font-semibold text-hsecondary">
            3. Information Sharing and Disclosure
          </h2>
          <p className="mt-2 pl-5 mb-4 text-md text-hsecondary font-bold">
            We Share Information Only:
          </p>
          <ul className="mt-2 list-disc pl-10 text-sm text-hmuted space-y-1">
            <li>
              <strong className="text-hsecondary">With Healthcare Providers:</strong> As necessary
              for your treatment and care coordination
            </li>
            <li>
              <strong className="text-hsecondary">With Insurance Companies:</strong> For claims
              processing and coverage verification
            </li>
            <li>
              <strong className="text-hsecondary">With Service Providers:</strong> Trusted third
              parties who assist in our operations (e.g., delivery services, payment processors)
            </li>
            <li>
              <strong className="text-hsecondary">As Required by Law:</strong> To comply with legal
              obligations, court orders, or regulatory requirements
            </li>
            <li>
              <strong className="text-hsecondary">For Public Health:</strong> As required for
              reporting adverse drug events or public health emergencies
            </li>
          </ul>

          <div className="mt-6 border-l-4 border-l-hprimary-600 bg-hprimary-600/10 p-4 text-sm text-hsecondary">
            <span className="font-bold">We never sell your personal or health information</span> to
            third parties for marketing purposes.
          </div>
        </section>

        {/* 4 */}
        <section className="mt-6">
          <h2 className="text-lg font-semibold text-hsecondary">4. Your Privacy Rights</h2>
          <p className="mt-2 pl-5 mb-4 text-sm text-hmuted">
            Under HIPAA and other privacy laws, you have the right to:
          </p>
          <ul className="mt-2 list-disc pl-10 text-sm text-hmuted space-y-1 py-2">
            <li>
              <strong className="text-hsecondary">Access:</strong> Request copies of your health
              information
            </li>
            <li>
              <strong className="text-hsecondary">Amendment:</strong> Request corrections to your
              health information
            </li>
            <li>
              <strong className="text-hsecondary">Accounting:</strong> Request a list of disclosures
              of your health information
            </li>
            <li>
              <strong className="text-hsecondary">Restrictions:</strong> Request limits on how we
              use or share your information
            </li>
            <li>
              <strong className="text-hsecondary">Confidential Communications:</strong> Request to
              receive communications in a specific way
            </li>
            <li>
              <strong className="text-hsecondary">Opt-Out:</strong> Unsubscribe from marketing
              communications
            </li>
            <li>
              <strong className="text-hsecondary">Data Deletion:</strong> Request deletion of
              certain personal information (subject to legal and regulatory requirements)
            </li>
          </ul>
          <p className="mt-2 text-sm text-hmuted">
            To exercise these rights, please contact us at 732-787-6666 or
            Holmdelpharmacycare@gmail.com.
          </p>
        </section>

        {/* 5 */}
        <section className="mt-6">
          <h2 className="text-lg font-semibold text-hsecondary">5. Data Security</h2>
          <p className="mt-2 pl-5 mb-4 text-sm text-hmuted">
            We implement comprehensive security measures to protect your information:
          </p>
          <ul className="mt-2 list-disc pl-10 text-sm text-hmuted space-y-1 py-2">
            <li>Encryption of data in transit and at rest</li>
            <li>Secure servers and network infrastructure</li>
            <li>Regular security audits and updates</li>
            <li>Staff training on privacy and security practices</li>
            <li>Restricted access to sensitive information</li>
            <li>Secure disposal of physical and electronic records</li>
          </ul>
          <p className="mt-2 text-sm text-hmuted">
            While we strive to protect your information, no method of transmission over the internet
            is 100% secure. We encourage you to use strong passwords and protect your account
            credentials.
          </p>
        </section>

        {/* 6 */}
        <section className="mt-6">
          <h2 className="text-lg font-semibold text-hsecondary">
            6. Cookies and Tracking Technologies
          </h2>
          <p className="mt-2 pl-5 mb-4 text-sm text-hmuted">
            We use cookies and similar technologies to enhance your experience on our website:
          </p>
          <ul className="mt-2 list-disc pl-10 text-sm text-hmuted space-y-1 py-2">
            <li>
              <strong className="text-hsecondary">Essential Cookies:</strong> Required for website
              functionality
            </li>
            <li>
              <strong className="text-hsecondary">Analytics Cookies:</strong> Help us understand how
              visitors use our site
            </li>
            <li>
              <strong className="text-hsecondary">Preference Cookies:</strong> Remember your
              settings and preferences
            </li>
          </ul>
          <p className="mt-2 text-sm text-hmuted">
            You can control cookies through your browser settings, but disabling certain cookies may
            limit website functionality.
          </p>
        </section>

        {/* 7 */}
        <section className="mt-6">
          <h2 className="text-lg font-semibold text-hsecondary">7. Children&apos;s Privacy</h2>
          <p className="mt-2 text-sm text-hmuted">
            Our services are not directed to children under 13. We do not knowingly collect personal
            information from children under 13 without parental consent. If you believe we have
            inadvertently collected such information, please contact us immediately.
          </p>
        </section>

        {/* 8 */}
        <section className="mt-6">
          <h2 className="text-lg font-semibold text-hsecondary">8. Data Retention</h2>
          <p className="mt-2 pl-5 mb-4 text-sm text-hmuted">
            We retain your information for as long as necessary to:
          </p>
          <ul className="mt-2 list-disc pl-10 text-sm text-hmuted space-y-1 py-2">
            <li>Provide our services and maintain your account</li>
            <li>Comply with legal, regulatory, and professional obligations</li>
            <li>Resolve disputes and enforce our agreements</li>
            <li>Maintain accurate medical and business records</li>
          </ul>
          <p className="mt-2 text-sm text-hmuted">
            Prescription records are maintained in accordance with state and federal pharmacy
            regulations.
          </p>
        </section>

        {/* 9 */}
        <section className="mt-6">
          <h2 className="text-lg font-semibold text-hsecondary">
            9. Changes to This Privacy Policy
          </h2>
          <p className="mt-2 text-sm text-hmuted">
            We may update this Privacy Policy from time to time. We will notify you of any material
            changes by posting the new policy on this page and updating the &quot;Last Updated&quot;
            date. We encourage you to review this policy periodically.
          </p>
        </section>

        {/* 10 */}
        <section className="mt-6">
          <h2 className="text-lg font-semibold text-hsecondary">10. Contact Us</h2>
          <p className="mt-2 pl-5 mb-4 text-sm text-hmuted py-2">
            If you have questions about this Privacy Policy or our privacy practices, or to exercise
            your privacy rights, please contact:
          </p>

          <div className="mt-3 rounded-xl border bg-hprimary/5 p-6 text-sm text-hmuted">
            <p className="font-bold text-hsecondary py-2">Privacy Officer</p>
            <p className="font-bold py-2 text-hsecondary">Holmdel Pharmacy Care</p>
            <p>2145 Route 35, Holmdel, NJ 07733</p>
            <p>Phone: 732-787-6666</p>
            <p>Email: holmdelpharmacycare@gmail.com</p>
          </div>
          <p className="mt-2 text-sm text-hmuted py-2">
            For formal HIPAA privacy complaints, you may also contact the U.S. Department of Health
            and Human Services.
          </p>
        </section>

        <div className="w-full border-b border-gray-200 mt-12" />

        {/* Footer */}
        <div className="mt-10">
          <Link
            href={buildPath('/')}
            className="inline-flex rounded-full bg-hprimary px-6 py-2 text-sm font-bold text-white hover:opacity-80"
          >
            Return to Home
          </Link>
        </div>
      </div>
    </main>
  );
}
